import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';

import { demoPermissions, demoTeams } from '@/data/demo-teams';
import type { Permission } from '@/types/teams';
import { getApiUrl } from '@/lib/api-config';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { authFetch } from '@/lib/auth-fetch';
import { getModuleMode } from '@/lib/feature-mode';

const getApiBaseUrl = (): string => {
  return getApiUrl() ?? getBackendUrl();
};

// Helper to check if all permissions should be bypassed
// This is extracted to a function to make it testable with Jest
const getBypassAllPermissions = (): boolean => {
  // Check Vite environment variable for explicit opt-in to bypass permissions
  // Use try-catch to handle Jest environments where import.meta.env may not be available
  try {
    if (import.meta?.env?.VITE_ENABLE_ALL_PERMISSIONS === 'true') {
      return true;
    }
  } catch {
    // In Jest environment, import.meta.env may not be available
  }
  
  // Also bypass permissions in demo mode (when no backend is configured)
  // This allows users to see all menu items when exploring the demo
  try {
    const assetsMode = getModuleMode('assets');
    if (assetsMode === 'demo') {
      return true;
    }
  } catch {
    // If feature mode check fails, don't bypass
  }
  
  return false;
};

// Helper to check if we're in development mode
const isDevMode = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.DEV === true;
    }
  } catch {
    // In Jest environment, import.meta.env may not be available
  }
  return false;
};

export interface UserPermissions {
  userId: string;
  teamIds: string[];
  grantedPermissions: string[];
  grantedSubPermissions: string[];
  permissionsEnforced?: boolean;
}

export interface PermissionsContextValue {
  permissions: Permission[];
  userPermissions: UserPermissions | null;
  loading: boolean;
  error: Error | null;
  permissionsEnforced: boolean;
  hasPermission: (screenKey: string) => boolean;
  hasSubPermission: (subPermissionKey: string) => boolean;
  hasAnyPermissionInCategory: (category: string) => boolean;
  canAccessRoute: (route: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  permissions: [],
  userPermissions: null,
  loading: true,
  error: null,
  permissionsEnforced: true,
  hasPermission: () => false, // Deny by default (secure)
  hasSubPermission: () => false, // Deny by default (secure)
  hasAnyPermissionInCategory: () => false, // Deny by default (secure)
  canAccessRoute: () => false, // Deny by default (secure)
  refreshPermissions: async () => { },
});

export function usePermissions(): PermissionsContextValue {
  return useContext(PermissionsContext);
}

// Map routes to permission screen keys
const routeToPermissionMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/assets': 'assets',
  '/assets/import': 'import-assets',
  '/assets/import/csv': 'import-assets',
  '/assets/import/accounting-sync': 'import-assets',
  '/assets/import/transaction-scan': 'import-assets',
  '/transactions/additions': 'additions',
  '/transactions/depreciation': 'depreciation',
  '/transactions/revaluation': 'revaluation',
  '/transactions/disposals': 'disposals',
  '/tasks': 'tasks',
  '/tasks/home': 'tasks',
  '/checkinout': 'checkinout',
  '/checkinout/calendar': 'checkinout',
  '/checkinout/checkout': 'checkinout',
  '/checkinout/settings': 'checkinout',
  '/compliance': 'compliance',
  '/compliance/assets': 'compliance',
  '/compliance/regulations': 'compliance',
  '/compliance/schedule': 'compliance',
  '/audits': 'audits-manager',
  '/audits/dashboard': 'audits-manager',
  '/audits/active': 'audits-manager',
  '/audits/scheduled': 'audits-manager',
  '/audits/history': 'audits-manager',
  '/audits/exceptions': 'audits-manager',
  '/audits/create': 'audits-manager',
  '/insights': 'insights',
  '/enquiry': 'enquiry',
  '/reports/insurance-value': 'insights',
  '/reports/statutory': 'insights',
  '/audit': 'audit-trail',
  '/administration/imports': 'import-assets',
  '/administration/import-transactions': 'import-transactions',
  '/administration/locations': 'locations',
  '/administration/categories': 'categories',
  '/administration/equipment': 'equipment',
  '/administration/disposal-reasons': 'disposal-reasons',
  '/administration/teams-permissions': 'team-settings',
  '/settings/features': 'feature-settings',
  '/settings/asset-options': 'asset-options',
  '/settings/integration-log': 'integration-log',
  '/settings/period-management': 'period-management',
  '/help': 'help',
};

// Routes that should always be accessible regardless of permissions
// These are general utility screens that don't require specific access rights
const alwaysAccessibleRoutes: Set<string> = new Set([
  '/help',
]);

interface PermissionsProviderProps {
  readonly children: ReactNode;
  readonly userId?: string;
  readonly bypassPermissions?: boolean;
}

export function PermissionsProvider({ children, userId = 'current_user', bypassPermissions }: PermissionsProviderProps): React.ReactElement {
  // Enable all permissions via Vite environment variable (explicit opt-in for development)
  // Set VITE_ENABLE_ALL_PERMISSIONS=true in your .env file to bypass permission checks
  const BYPASS_ALL_PERMISSIONS = bypassPermissions === true || getBypassAllPermissions();

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Load permission definitions and user permissions
  const loadPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Only bypass permissions if explicitly enabled via environment variable
      if (BYPASS_ALL_PERMISSIONS) {
        setPermissions(demoPermissions);
        const allPermIds = demoPermissions.map(p => p.id);
        const allSubPermIds = demoPermissions.flatMap(p => (p.subPermissions ?? []).map(sp => sp.id));

        setUserPermissions({
          userId,
          teamIds: ['bypass_all_permissions'],
          grantedPermissions: allPermIds,
          grantedSubPermissions: allSubPermIds.filter((id): id is string => id !== undefined),
        });
        setLoading(false);
        return;
      }

      // Try to fetch permissions from the backend API first
      // This ensures we use the user's actual team permissions when a backend is available
      const apiBaseUrl = getApiBaseUrl();

      try {
        const [permResponse, userPermResponse] = await Promise.all([
          authFetch(`${apiBaseUrl}/api/teams/permissions`),
          authFetch(`${apiBaseUrl}/api/teams/current-user/permissions`),
        ]);

        if (permResponse.ok && userPermResponse.ok) {
          const permData = await permResponse.json();
          const userPermData = await userPermResponse.json();

          if (userPermData.permissionsEnforced === false) {
            setPermissions(permData);
            const allPermIds = permData.map((p: { id: string }) => p.id);
            const allSubPermIds = permData.flatMap(
              (p: { subPermissions?: Array<{ id?: string }> }) =>
                (p.subPermissions ?? []).map((sp: { id?: string }) => sp.id)
            );
            setUserPermissions({
              userId: userPermData.userId ?? 'bypass',
              teamIds: ['bypass_all_permissions'],
              grantedPermissions: allPermIds,
              grantedSubPermissions: allSubPermIds.filter((id: string | undefined): id is string => id !== undefined),
            });
            setLoading(false);
            return;
          }

          setPermissions(permData);
          setUserPermissions(userPermData);
          setLoading(false);
          return;
        }

        // If API calls failed, fall through to demo mode
        console.warn('⚠️ Backend API not available, falling back to demo mode');
      } catch (apiError) {
        // Network error or other issue - fall through to demo mode
        console.warn('⚠️ Failed to fetch from backend API, falling back to demo mode:', apiError);
      }

      // Fallback behavior depends on environment
      setPermissions(demoPermissions);
      await new Promise(resolve => setTimeout(resolve, 200));

      // SECURITY: Only use demo data fallback in development mode
      // In production, deny all permissions when backend is unavailable (fail secure)
      if (isDevMode()) {
        // Dev mode: use Standard Users team permissions from demo data
        const standardTeam = demoTeams.find(t => t.id === 'team_default_standard');
        if (standardTeam) {
          const grantedPermissions: string[] = [];
          const grantedSubPermissions: string[] = [];

          standardTeam.permissions.forEach(perm => {
            if (perm.granted && perm.permissionId) {
              grantedPermissions.push(perm.permissionId);
              perm.subPermissions?.forEach(subPerm => {
                if (subPerm.granted && subPerm.id) {
                  grantedSubPermissions.push(subPerm.id);
                }
              });
            }
          });

          setUserPermissions({
            userId,
            teamIds: [standardTeam.id],
            grantedPermissions,
            grantedSubPermissions,
          });
        } else {
          // Fallback - deny all permissions if team not found
          console.warn('⚠️ Standard Users team not found, denying all permissions');
          setUserPermissions({
            userId,
            teamIds: [],
            grantedPermissions: [],
            grantedSubPermissions: [],
          });
        }
      } else {
        // Production: deny all permissions when backend is unavailable (fail secure)
        console.warn('🔒 PRODUCTION MODE: Backend unavailable, denying all permissions for security');
        setUserPermissions({
          userId,
          teamIds: [],
          grantedPermissions: [],
          grantedSubPermissions: [],
        });
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));

      // SECURITY: On error, deny all permissions (fail secure)
      // Do NOT grant full access on error - this is a security risk
      setPermissions(demoPermissions);
      console.warn('⚠️ Permission loading failed, denying all permissions for security');
      setUserPermissions({
        userId,
        teamIds: [],
        grantedPermissions: [],
        grantedSubPermissions: [],
      });
    } finally {
      setLoading(false);
    }
  }, [userId, BYPASS_ALL_PERMISSIONS]);

  const permissionsEnforced = !BYPASS_ALL_PERMISSIONS
    && (userPermissions?.permissionsEnforced !== false);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Check if user has permission to a screen
  // SECURITY: Returns false when permissions are not loaded (deny by default)
  // Use the `loading` state to show loading UI while permissions are being fetched
  const hasPermission = useCallback((screenKey: string): boolean => {
    if (!permissionsEnforced) return true;
    // Deny access if permissions not loaded yet
    if (!userPermissions || !Array.isArray(userPermissions.grantedPermissions)) return false;
    // Deny access if permission definitions not loaded yet
    if (permissions.length === 0) return false;

    // Find the permission definition for this screen
    const permDef = permissions.find(p => p.screenKey === screenKey);
    if (!permDef) return true; // If no permission defined for this screen, allow access (screen not protected)

    // Check if user has this permission granted
    // Use case-insensitive comparison for GUIDs since SQL Server stores them in uppercase
    const permDefIdLower = permDef.id.toLowerCase();
    return userPermissions.grantedPermissions.some(
      (id: string) => id.toLowerCase() === permDefIdLower
    );
  }, [permissions, userPermissions, permissionsEnforced]);

  // Check if user has a specific sub-permission
  // SECURITY: Returns false when permissions are not loaded (deny by default)
  // Use the `loading` state to show loading UI while permissions are being fetched
  const hasSubPermission = useCallback((subPermissionKey: string): boolean => {
    if (!permissionsEnforced) return true;
    // Deny access if permissions not loaded yet
    if (!userPermissions) return false;
    // Deny access if permission definitions not loaded yet
    if (permissions.length === 0) return false;

    // Find the sub-permission definition
    for (const perm of permissions) {
      const subPerm = (perm.subPermissions ?? []).find(sp => sp.key === subPermissionKey);
      if (subPerm?.id) {
        // Use case-insensitive comparison for GUIDs since SQL Server stores them in uppercase
        const subPermIdLower = subPerm.id.toLowerCase();
        return userPermissions.grantedSubPermissions.some(
          (id: string) => id.toLowerCase() === subPermIdLower
        );
      }
    }

    // SECURITY: If sub-permission key not found in definitions, deny access (secure default)
    // This prevents access when permission definitions are incomplete or key is misspelled
    return false;
  }, [permissions, userPermissions, permissionsEnforced]);

  // Check if user has any permission in a category
  // SECURITY: Returns false when permissions are not loaded (deny by default)
  // Use the `loading` state to show loading UI while permissions are being fetched
  const hasAnyPermissionInCategory = useCallback((category: string): boolean => {
    if (!permissionsEnforced) return true;
    // Deny access if permissions not loaded yet
    if (!userPermissions) return false;
    // Deny access if permission definitions not loaded yet
    if (permissions.length === 0) return false;

    const categoryPerms = permissions.filter(p => p.category === category);
    if (categoryPerms.length === 0) return true; // If no permissions in category, allow access (category not protected)

    return categoryPerms.some(p => userPermissions.grantedPermissions.includes(p.id));
  }, [permissions, userPermissions, permissionsEnforced]);

  // Check if user can access a specific route
  // SECURITY: Returns false when permissions are not loaded (deny by default)
  // Use the `loading` state to show loading UI while permissions are being fetched
  const canAccessRoute = useCallback((route: string): boolean => {
    if (!permissionsEnforced) return true;
    // Always allow access to routes that don't require permissions
    if (alwaysAccessibleRoutes.has(route)) return true;

    if (!userPermissions) return false; // Deny access while loading (secure default)

    // Find the permission screen key for this route
    const screenKey = routeToPermissionMap[route];
    if (!screenKey) return true; // If no permission mapping, allow access

    return hasPermission(screenKey);
  }, [hasPermission, userPermissions, permissionsEnforced]);

  const refreshPermissions = useCallback(async () => {
    await loadPermissions();
  }, [loadPermissions]);

  const contextValue = useMemo(() => ({
    permissions,
    userPermissions,
    loading,
    error,
    permissionsEnforced,
    hasPermission,
    hasSubPermission,
    hasAnyPermissionInCategory,
    canAccessRoute,
    refreshPermissions,
  }), [
    permissions,
    userPermissions,
    loading,
    error,
    permissionsEnforced,
    hasPermission,
    hasSubPermission,
    hasAnyPermissionInCategory,
    canAccessRoute,
    refreshPermissions,
  ]);

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  );
}
